import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '@/services/appApi';

export interface BootstrapSymbol {
  symbol: string;
  name: string;
  enabled: boolean;
  max_leverage: number;
  spread_points: number;
}

export interface AppBootstrapConfig {
  version: string;
  platform: string;
  app_version: string;
  symbols: BootstrapSymbol[];
  feature_flags: {
    manual_order: boolean;
    copy_trading: boolean;
    two_factor_auth: boolean;
    avatar_upload: boolean;
  };
  risk_config: {
    max_recent_order_query_days: number;
    default_max_drawdown_pct: number;
    default_max_lot_per_trade: number;
  };
  query_policy: {
    recent_order_range_days: number;
    default_page_size: number;
    max_page_size: number;
  };
  compliance_copy: {
    risk_disclosure_version: string;
    terms_version: string;
    privacy_version: string;
  };
  security_config: {
    two_factor_optional: boolean;
    two_factor_required_after_enabled: boolean;
  };
}

interface AppConfigContextValue {
  config: AppBootstrapConfig | null;
  loading: boolean;
  error: string | null;
  refreshConfig: () => Promise<AppBootstrapConfig | null>;
}

const AppConfigContext = createContext<AppConfigContextValue>({
  config: null,
  loading: true,
  error: null,
  refreshConfig: async () => null,
});

function platform(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'web';
}

export function AppConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AppBootstrapConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshConfig = async () => {
    try {
      setError(null);
      const params = new URLSearchParams({
        platform: platform(),
        app_version: import.meta.env.VITE_APP_VERSION || '0.0.0',
      });
      const next = await apiRequest<AppBootstrapConfig>(`/app/config/bootstrap?${params.toString()}`);
      setConfig(next);
      return next;
    } catch (err) {
      setError(err instanceof Error ? err.message : '配置拉取失败');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refreshConfig();
  }, []);

  const value = useMemo<AppConfigContextValue>(() => ({
    config,
    loading,
    error,
    refreshConfig,
  }), [config, loading, error]);

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
}

export function useAppConfig() {
  return useContext(AppConfigContext);
}
